import type { Interlocutor } from "../../../types/conversationInbox";

import type { InboxV2Account, InboxV2Conversation } from "../../types";

export interface InboxV2MessageMappingOptions {
  account?: InboxV2Account;
  conversationAccountId?: string;
}

export type InboxV2Identity = {
  avatarUrl?: string;
  handle: string;
  id?: string;
  isConversationAccountSender: boolean;
  isUnknownSource: boolean;
  name: string;
  platform?: InboxV2Conversation["platform"];
  platformId?: string;
  fetchedError?: string;
  sentFromSked: boolean;
  sentFromSkedUserName?: string;
};

export function mapPlatformFromSenderPlatform(
  platform?: string
): InboxV2Conversation["platform"] | undefined {
  const normalizedPlatform = platform?.toLowerCase();

  if (!normalizedPlatform || normalizedPlatform === "sk") {
    return undefined;
  }

  if (normalizedPlatform.startsWith("ig")) {
    return "instagram";
  }

  if (normalizedPlatform.startsWith("fb")) {
    return "facebook";
  }

  if (normalizedPlatform.startsWith("li")) {
    return "linkedin";
  }

  if (normalizedPlatform.startsWith("tt")) {
    return "tiktok";
  }

  if (
    normalizedPlatform.startsWith("gmb") ||
    normalizedPlatform.startsWith("gb")
  ) {
    return "google-business";
  }

  return undefined;
}

export function mapInboxV2Identity(
  source: Partial<Interlocutor> | string | undefined,
  options: {
    account?: InboxV2Account;
    sentFromSked?: boolean;
  } = {}
): InboxV2Identity {
  const sender = typeof source === "string" ? { name: source } : source;
  const sentFromSked = Boolean(options.sentFromSked);
  const isConversationAccountSender = isInboxV2IdentityFromAccount(
    {
      platform: sender?.platform,
      platformId: sender?.platformId,
      sentFromSked,
    },
    options.account
  );

  return {
    avatarUrl: sender?.image,
    handle: normalizeHandle(sender?.handle),
    id: sender?.id,
    isConversationAccountSender,
    isUnknownSource: Boolean(sender?.isUnknownSource),
    name: sender?.name ?? "Unknown user",
    platform: mapPlatformFromSenderPlatform(sender?.platform),
    platformId: sender?.platformId,
    fetchedError: sender?.fetchedError,
    sentFromSked,
    sentFromSkedUserName: sentFromSked ? sender?.name : undefined,
  };
}

export function mapOptionalInboxV2Identity(
  source: Partial<Interlocutor> | string | undefined,
  options: {
    account?: InboxV2Account;
    sentFromSked?: boolean;
  } = {}
) {
  return source ? mapInboxV2Identity(source, options) : undefined;
}

export function isInboxV2IdentityFromAccount(
  identity:
    | {
        isAccount?: boolean;
        platform?: string;
        platformId?: string;
        sentFromSked?: boolean;
      }
    | undefined,
  account?: InboxV2Account
) {
  if (
    identity?.isAccount ||
    identity?.sentFromSked ||
    identity?.platform === "SK"
  ) {
    return true;
  }

  const normalizedIdentityPlatformId = normalizeIdentity(identity?.platformId);
  const normalizedAccountPlatformId = normalizeIdentity(account?.platformId);

  if (!normalizedIdentityPlatformId || !normalizedAccountPlatformId) {
    return false;
  }

  if (normalizedIdentityPlatformId === normalizedAccountPlatformId) {
    return true;
  }

  if (account?.platform === "linkedin") {
    return [
      `urn:li:organization:${normalizedAccountPlatformId}`,
      `urn:li:person:${normalizedAccountPlatformId}`,
    ].includes(normalizedIdentityPlatformId);
  }

  return false;
}

function normalizeHandle(handle?: string) {
  return handle?.replace(/^@+/, "") ?? "";
}

function normalizeIdentity(value?: string) {
  return value?.replace(/^@+/, "").trim().toLowerCase() ?? "";
}
